// Meta Pixel utility — direct injection of fbq base code (no external deps)
// Pixel ID: 3326804787497839
// Same pixel as connected on Shopify to allow proper attribution from landing -> checkout
//
// Events fired from the landing:
//  - PageView       (on page load)
//  - ViewContent    (on page load)
//  - InitiateCheckout (on CTA click)
//
// IMPORTANT: Purchase is NOT fired from the landing.
// Purchase is fired by Shopify on the order confirmation / thank-you page.
//
// All events include a unique `eventID` for server-side deduplication
// (Conversions API). The eventID is also persisted into sessionStorage so
// Shopify can read & forward it for the matching server-side event.

declare global {
  interface Window {
    fbq?: any
    _fbq?: any
  }
}

const PIXEL_ID = '3326804787497839'

let initialized = false

// ---------- Helpers ----------

function generateEventId(prefix: string): string {
  const rand =
    typeof crypto !== 'undefined' && (crypto as any).randomUUID
      ? (crypto as any).randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  return `${prefix}_${rand}`
}

function persistEventId(key: string, eventId: string): void {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.setItem(`fb_event_id__${key}`, eventId)
    }
  } catch {
    /* sessionStorage unavailable — ignore */
  }
}

export function getStoredEventId(key: string): string | null {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      return window.sessionStorage.getItem(`fb_event_id__${key}`)
    }
  } catch {
    /* ignore */
  }
  return null
}

// ---------- Init ----------

export function initMetaPixel(): void {
  if (typeof window === 'undefined' || initialized) return
  if (window.fbq) {
    initialized = true
    return
  }

  // Meta Pixel base code — safe variant.
  // The official snippet does:
  //   s = b.getElementsByTagName('script')[0]; s.parentNode.insertBefore(t, s)
  // which crashes with "Cannot read properties of undefined (reading 'parentNode')"
  // in environments where there is no pre-existing <script> tag in the DOM
  // (e.g. SPA preview iframes, module-only loaders). We append the pixel script
  // to <head> directly instead.
  const w = window as any
  const fbq: any = function () {
    // eslint-disable-next-line prefer-rest-params
    fbq.callMethod
      ? fbq.callMethod.apply(fbq, arguments as any)
      : fbq.queue.push(arguments)
  }
  w.fbq = fbq
  if (!w._fbq) w._fbq = fbq
  fbq.push = fbq
  fbq.loaded = true
  fbq.version = '2.0'
  fbq.queue = []

  const pixelScript = document.createElement('script')
  pixelScript.async = true
  pixelScript.src = 'https://connect.facebook.net/en_US/fbevents.js'
  const target = document.head || document.body || document.documentElement
  if (target) target.appendChild(pixelScript)

  window.fbq('init', PIXEL_ID)
  initialized = true

  // <noscript> fallback
  if (!document.getElementById('fb-pixel-noscript')) {
    const noscript = document.createElement('noscript')
    noscript.id = 'fb-pixel-noscript'
    const img = document.createElement('img')
    img.height = 1
    img.width = 1
    img.style.display = 'none'
    img.src = `https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`
    noscript.appendChild(img)
    document.body.appendChild(noscript)
  }
}

// ---------- Events ----------

export function trackPageView(): string {
  const eventId = generateEventId('PageView')
  persistEventId('PageView', eventId)
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'PageView', {}, { eventID: eventId })
  }
  return eventId
}

export function trackViewContent(): string {
  const eventId = generateEventId('ViewContent')
  persistEventId('ViewContent', eventId)
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq(
      'track',
      'ViewContent',
      {
        content_name: 'Bajá de peso comiendo así todos los días',
        content_category: 'Ebook / Recetas Air Fryer',
        content_ids: ['ebook-air-fryer-1000-recetas'],
        content_type: 'product',
        value: 12990,
        currency: 'ARS',
      },
      { eventID: eventId },
    )
  }
  return eventId
}

export function trackInitiateCheckout(): string {
  const eventId = generateEventId('InitiateCheckout')
  persistEventId('InitiateCheckout', eventId)
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq(
      'track',
      'InitiateCheckout',
      {
        content_name: 'Bajá de peso comiendo así todos los días',
        content_ids: ['ebook-air-fryer-1000-recetas'],
        content_category: 'Ebook / Recetas Air Fryer',
        num_items: 1,
        value: 12990,
        currency: 'ARS',
      },
      { eventID: eventId },
    )
  }
  return eventId
}

// Fired on every CTA / buy-link click.
// Only InitiateCheckout — Purchase is handled by Shopify on the thank-you page.
export function trackCheckoutClick(): void {
  trackInitiateCheckout()
}
